/* eslint-disable no-unused-vars */
import React, { useState, useLayoutEffect, useContext, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TableCell, TableRow } from '@material-ui/core';
import { isMobileOnly, isTablet } from 'react-device-detect';
import _ from 'lodash';
import classNames from 'classnames';

import RacingDetailContext from '../../Context/RacingDetail.context';
import { RaceEvent, RaceCompetitor, RaceOutcome } from '../../../../interfaces/RacingState';
import { RootState } from '../../../../configs/store';
import AppContext from 'src/App.context';
import { selectionTypes } from '../../../../utils/content';
import { allGrayedOut, checkIfCrossEvent, validName } from '../../../../utils/racingCombinations';
import formatOdds from '../../../../utils/formatOdds';
import ExpandedRow from './ExpandedRow/ExpandedRow';
import MultiCheckboxCell from './MultiCheckboxCell/MultiCheckboxCell';
import SingleCheckboxCell from './SingleCheckboxCell/SingleCheckboxCell';
import OddsCell from './OddsCell/OddsCell';
import RankCell from './RankCell/RankCell';
import QuickBetHorseCell from './QuickBetHorseCell/QuickBetHorseCell';
import SingleBetHorseCell from './SingleBetHorseCell/SingleBetHorseCell';
import BetHorseCellsDesktop from './BetHorseCellsDesktop/BetHorseCellsDesktop';
import StartNoCell from './StartNoCell/StartNoCell';

interface CompetitorsTableRowProps {
  event?: RaceEvent,
  competitor: RaceCompetitor,
  selectedMarketIndex: number,
  selectedMarketName: string,
  crossEvent: any,
  ignoreSelectedMarket?: boolean
  isPick?: boolean,
  leg?: number,
  official?: boolean,
  podiumNr?: number,
  meeting?: any,
  racingType?: string,
  shouldWeight: boolean,
  shouldJockey: boolean,
  ellipsed?: boolean,
  payouts?: any
}

export default function CompetitorsTableRow({
  event,
  competitor,
  selectedMarketIndex,
  selectedMarketName,
  crossEvent,
  leg,
  isPick=false,
  meeting,
  racingType,
  shouldWeight,
  shouldJockey,
  payouts,
  ellipsed=false
}: CompetitorsTableRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [marketCode, setMarketCode] = useState('');
  const rowRef = useRef(null);
  const {
    selectedMarket,
    selectedSelectionType,
    setSelectedHorsePosition,
    selectedHorsePosition,
    isValidForQuickBet,
    official,
    podiumNr
  } = useContext(RacingDetailContext);
  const { isPortrait } = useContext(AppContext);

  const { selectedOddsType } = useSelector((state: RootState) => state.odds);
  const { bets } = useSelector((state: RootState) => state.betslip);
  const { meeting: { name, countryCode } } = useSelector((state: RootState) => state.racing);
  const dispatch = useDispatch();

  let horseResults = '';
  if (competitor.horse && competitor.horse.horsePerformance) {
    horseResults = _.map(Object.values(competitor.horse.horsePerformance), (race) => { return race.place; }).join(' - ');
  }

  //#region FUNCTIONS
  useLayoutEffect(() => {
    if (selectedMarket.code !== marketCode) {
      setExpanded(false);
      setMarketCode(selectedMarket.code.toString());
    }

  }, [selectedMarket]);

  useLayoutEffect(() => {
    if (expanded === true && !_.isNull(rowRef.current)) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [expanded]);

  const getOdds = (outcome) => {
    if ((outcome.oddsType === 2) && !(outcome.name).includes('SP')) {
      return 'TP';
    } else if ((outcome.name).includes('SP')) {
      return 'SP';
    }
    return outcome.value;
  };

  const addToBetslip = (outcome) => async (e?) => {
    e?.preventDefault();
    const marketOutcome = _.find(selectedMarket.outcomes, { name: outcome.name });
    const evt = event || crossEvent;

    await dispatch.betslip.addBet({
      eventCode: evt.eventCode,
      odds: outcome.value,
      marketOutcomeCode: outcome.code,
      marketCode: (selectedMarket.oddsType === 3) || selectedMarket.typeCode === 15100 ? outcome?.marketCode : selectedMarket.code,
      marketName: (selectedMarket.oddsType === 3) || selectedMarket.typeCode === 15100 ? outcome?.marketName : selectedMarket.name,
      tournamentName: `${ name } ${ countryCode }`,
      marketStatusNumber: selectedMarket.statusNumber,
      marketStatusName: selectedMarket.statusName,
      outcomeStatusNumber: outcome.statusNumber,
      outcomeStatusName: outcome.statusName,
      datetime: evt.eventStart ? evt.eventStart * 1000 : 0,
      sport: '',
      outcome: marketOutcome?.name || competitor.outcomes[0]?.name || competitor.name,
      genericName: (selectedMarket.oddsType === 3 && !marketOutcome)
      || selectedMarket.typeCode === 15100 ? outcome?.marketGenericName : selectedMarket.genericName,
      type: 'racing',
      productType: 2,
      startNo: competitor.progNo,
      prediction: {
        numbersToPredictCount: selectedMarket.predictionCount,
        anyNumbers: [competitor.startNo],
        rankedNumbers:[],
        inOrder: false
      },
      jsonLabel: {
        id: competitor.progNo,
        competitor: marketOutcome?.name || competitor.outcomes[0]?.name || competitor.name
      },
      oddsType: selectedMarket.oddsType === 2 && _.isNaN(outcome.value) ? 2 : 3,
      predictionCount: selectedMarket.predictionCount,
      raceNo: evt.raceNo,
      horseType: selectedMarket.oddsType === 2 && (selectedMarket.marketTypeId === 15100 || _.isNaN(outcome.value)) ? 'TP' : 'FO',
      eventName: meeting.name,
      country: meeting.country,
      racingType: racingType,
      venue: event['venueName']
    });
  };

  const removeFromBetslip = (index: number) => (e?) => {
    if (!e) {
      e.preventDefault();
    }

    dispatch.betslip.removeBetAtIndex(index);
  };

  const getPrettyName = (name: string) => {
    const nameArr = name ? name.split(' ') : '';

    if (nameArr === '') return '-';
    if (nameArr.length === 1) return nameArr[0];

    const newName = `${ nameArr[0] } ${ nameArr[nameArr.length - 1] }`;

    return newName;
  };

  const toteBetIndex = () => {
    if (!competitor.outcomes[0]) return -1;

    return _.findIndex(bets, {
      eventCode: event?.eventCode || crossEvent.eventCode,
      marketCode: selectedMarket.code,
      outcome: competitor.outcomes[0].name ? competitor.outcomes[0].name  : competitor.name,
      marketName: selectedMarket.name
    });
  };

  const toggleCheckbox = async () => {
    if (toteBetIndex() !== -1) {
      await dispatch.betslip.removeBetAtIndex(toteBetIndex());
    } else {
      const outcome = {
        code: selectedMarket.outcomes[0].code,
        marketName: selectedMarket.name,
        marketCode: selectedMarket.typeCode,
        outcomeCode: selectedMarket.outcomes[0].code,
        value: getOdds(selectedMarket)
      };

      addToBetslip(outcome)();
    }
  };

  const handleQuickBetToggle = (position: { name: string }, number:number) => () => {
    let newPositions = JSON.parse(JSON.stringify(selectedHorsePosition));
    let name = position.name === 'all'? selectedMarket.predictionCount : +position.name -1;

    if (position.name === 'all' && selectedSelectionType !== 'singlePerm'){
      name = selectedSelectionType !== 'boxed' && selectedSelectionType ? 1 : 0;
    }

    if (isPick){
      name = leg;
    }

    const index = _.findIndex(selectedHorsePosition[name], (horseNumber) => horseNumber === number);

    if (index < 0){
      if (position.name === 'all' && !leg) {
        //if We have positions selected and we select all, those positions should be removed
        newPositions = _.map(newPositions, (position) => _.filter(position, (horseNumber) => horseNumber !== number));
      } else if (!leg) {
        // if we have all checked and we select a position, then all should be removed
        const index = _.findIndex(newPositions[selectedMarket.predictionCount], (horseNumber) => horseNumber === number);
        if (index > 0){
          newPositions[selectedMarket.predictionCount].splice(index, 1);
        }
      }

      newPositions[name].push(number);
    } else {
      newPositions[name].splice(index, 1);
    }

    setSelectedHorsePosition(newPositions);
  };

  const pickOdds = () => {
    return checkIfCrossEvent(selectedMarket);
  };

  const singlePerm = () => {
    return validName(selectedMarket) === 'trifecta'
    || validName(selectedMarket) === 'superfecta'
    || validName(selectedMarket) === 'exacta'
    || validName(selectedMarket) === 'tricast'
    || validName(selectedMarket) === 'quartet'
    || selectedMarket.typeCode === 10500;
  };

  const handleTableRowSpan = () => {
    if (isValidForQuickBet && singlePerm() && !pickOdds() && selectedMarket.predictionCount > 1) {
      return 6 + selectionTypes['singlePerm'][validName(selectedMarket)].positions;
    } else if (isValidForQuickBet && !singlePerm() && !pickOdds() && selectedMarket.predictionCount > 1) {
      return 6 + selectionTypes[validName(selectedMarket)].positions;
    } else if (selectedMarketIndex !== 0 && selectedMarket.oddsType === 3) {
      return _.filter(competitor.outcomes, { show: true }).length + 6;
    } else {
      return 8;
    }
  };

  const renderValidHead = () => {
    const checkBoxes = [];
    const name = validName(selectedMarket);
    if (isValidForQuickBet) {
      let selectionType = selectedSelectionType !== 'singlePerm' ?
        selectionTypes[selectedSelectionType] :
        selectionTypes[selectedSelectionType][name];
      if (!selectionType) selectionType = selectionTypes[name];
      for (let i = 0; i < selectionType?.positions; i += 1) {
        checkBoxes.push({ name: i + 1 });
      }
      if (selectionType?.bankers) {
        checkBoxes.push({ name: 1 });
      }
      checkBoxes.push({ name: 'all' });
    }

    return checkBoxes;
  };
  //#endregion

  const checkCompetitor = (competitor) => {
    if (_.isNil(competitor.horse.name) && _.isNil(competitor.trainer.name) && _.isNil(competitor.horse.gender) && _.isNil(competitor.horse.owner)
    && _.isNil(competitor.horse.bornDate) && _.isNil(competitor.horse.color) && _.isNil(competitor.horse.ownerCountry)
    && _.isNil(competitor.horse.raisedCountry) && _.isNil(competitor.horse.bornCountry) && _.isNil(competitor.horse.sire)
    && _.isNil(competitor.horse.dam) && _.isNil(competitor.jockey.name) && _.isNil(competitor.jockey.country) || competitor.jockey.amateur !== 0
    && _.isNil(competitor.trainer.name) && _.isNil(competitor.trainer.country) && competitor.trainer.amateur !== 0) {
      return false;
    }

    return true;
  };

  const toggleExpand = (e) => {
    if (!e.target.classList.contains('competitors-table__market-odds')) {
      setExpanded(!expanded);
    }
  };

  const renderCheckbox = () => {
    if (selectedMarket.oddsType === 2 && selectedMarket.predictionCount === 1 && !isValidForQuickBet) {

      const isChecked = _.findIndex(selectedHorsePosition[0], (number) => number === competitor.startNo);

      return <SingleCheckboxCell
        marketStatus={ selectedMarket.statusNumber }
        checked={ isChecked !== -1 }
        onChange={ handleQuickBetToggle({ name: 'all' }, competitor.competitorId) }
      />;
    }

    if (selectedMarket.oddsType !== 3){
      return <TableCell style={ { width: '60px' } } />;
    }
    return null;
  };

  return (
    <>
      <TableRow
        ref={ rowRef }
        className={
          classNames({
            'competitors-table__scratch': competitor.statusNumber > 1 || (selectedMarket.statusNumber > 1 && selectedMarket.predictionCount > 1)
          })
        }
      >
        <StartNoCell
          startNo={ competitor.progNo }
          scratched={ competitor.statusNumber > 1 }
          { ...((isMobileOnly || (isTablet && isPortrait)) && { showShirt: !pickOdds() && !isValidForQuickBet }) }
        />

        {
          selectedMarket.oddsType !== 2
            ? (
              <SingleBetHorseCell
                onClick={ toggleExpand }
                competitor={ competitor }
                horseResults={ horseResults }
                formatName={ getPrettyName }
                showInfo={ !pickOdds() }
                expanded={ expanded }
                { ...(!(isMobileOnly || (isTablet && isPortrait)) && { showShirt: !pickOdds() && !isValidForQuickBet }) }
                checkCompetitor={ checkCompetitor(competitor) }
                official={ official }
                ellipsed = { ellipsed }
                position={ competitor.position }
                podiumNr={ podiumNr }
                racingType={ racingType }
              />
            )
            : (
              <QuickBetHorseCell
                onClick={ toggleExpand }
                competitor={ competitor }
                expanded={ expanded }
                ellipseName={ selectedSelectionType === 'singlePerm' && selectedMarketName !== 'EXACTA' }
                checkCompetitor={ checkCompetitor(competitor) }
                official={ official }
              />
            )
        }
        {
          !(isMobileOnly || (isTablet && isPortrait)) && (
            <BetHorseCellsDesktop
              onClick={ toggleExpand }
              competitor={ competitor }
              formatName={ getPrettyName }
              shouldWeight={ shouldWeight }
              shouldJockey={ shouldJockey }
              official={ official }
            />
          )
        }

        {
          _.map(_.filter(competitor.outcomes, { show: true }), (outcome: RaceOutcome, index) => {
            const betIndex = _.findIndex(bets, {
              marketCode: selectedMarket.oddsType === 3 || selectedMarket.typeCode === 15100 ? outcome.marketCode : selectedMarket.code,
              marketName: selectedMarket.oddsType === 3 || selectedMarket.typeCode === 15100 ? outcome.marketName : selectedMarket.name,
              outcome: outcome.name
            });
            if (!outcome.code){
              outcome.code = selectedMarket?.outcomes[0]?.code;
            }

            if ((!official && outcome.statusNumber >= 1 && competitor.statusNumber === 1 ||
              selectedMarket.typeCode !== 20100 && (selectedMarket.typeCode !== 15100 && competitor.statusNumber >= 1)) &&
              selectedMarket.statusNumber >=1 || selectedMarket.isScratchedSelectable){
              let isBlockedOutcome = false;

              for (const market of event.markets){
                const outcomeFound = _.find(market.outcomes, { code: outcome.code });
                if (outcomeFound){
                  isBlockedOutcome = outcomeFound.statusNumber > 1;
                }
              }

              return (
                <OddsCell
                  key={ outcome.name + outcome.value + index }
                  value={ outcome.value }
                  formattedValue={
                    isNaN(+outcome.value)
                    && outcome
                    && outcome?.value ? outcome.value.toString() : formatOdds(outcome.value, selectedOddsType)
                  }
                  oddsType={ selectedMarket.typeCode === 15100 ? 3 : selectedMarket.oddsType }
                  quickcharts= { ellipsed }
                  onClick={ betIndex !== -1 ? removeFromBetslip(betIndex) : addToBetslip(outcome) }
                  selected={ betIndex >= 0 }
                  official={ official }
                  blocked={ outcome.statusNumber > 1 || competitor.statusNumber > 1 || isBlockedOutcome }
                />
              );
            } else {
              const row = _.filter(payouts, { 'rank': competitor.position });
              const startingPrice = row['0']?.startingPrice;

              return (
                <OddsCell
                  key={ outcome.name + startingPrice + index }
                  value={ startingPrice }
                  formattedValue={ startingPrice ? formatOdds(startingPrice, selectedOddsType) : '0.0' }
                  oddsType={ selectedMarket.typeCode === 15100 ? 3 : selectedMarket.oddsType }
                  official = { true }
                />
              );
            }
          })
        }
        { official && <RankCell value={ competitor.position } podiumNr={ podiumNr } /> }
        { (selectedMarket.predictionCount <= 1 || isPick && competitor.outcomes.length < 1) && renderCheckbox() }
        {
          selectedMarket.oddsType === 2 && selectedMarket.predictionCount >= 1 &&
            _.map(renderValidHead(), (checkBox, index) => {
              const position = isPick ? leg : index;
              const isChecked = _.findIndex(selectedHorsePosition[position], (number) => number === competitor.startNo);
              const disabled = allGrayedOut(selectedHorsePosition, selectedSelectionType) && checkBox.name==='all';
              if ((competitor.statusNumber === 1) && selectedMarket.statusNumber >= 1) {
                return (
                  <MultiCheckboxCell
                    key={ index }
                    checked={ isChecked >= 0 }
                    onChange={ handleQuickBetToggle(checkBox, competitor.startNo) }
                    lastCheckbox={ checkBox.name === 'all' }
                    disabled={ disabled }
                    marketStatus={ selectedMarket.statusNumber }
                    marketStatusName={ selectedMarket.statusName }
                    statusNumber={ competitor.statusNumber }
                    statusName={ competitor.statusName }
                    isScratchedSelectable={ selectedMarket.isScratchedSelectable && pickOdds() }
                  />
                );
              } else {
                return null;
              }
            })
        }
        { /* {
          (selectedMarket.predictionCount > 1 && competitor.statusNumber > 1 && !selectedMarket.isScratchedSelectable) &&
          (<TableCell colSpan={ selectedMarket.predictionCount + 1 }>
            <div className="competitors-table__status">
              { competitor.statusNumber > 1 ? competitor.statusName : 'selectedMarket.statusName'  }
            </div>
          </TableCell>)
        }
        {
          (selectedMarket.predictionCount >= 1 && competitor.statusNumber > 1 && !selectedMarket.isScratchedSelectable) && (
            <TableCell colSpan={ selectedMarket.predictionCount + 1 }>
              <div className="competitors-table__status">
                { competitor.statusNumber > 1 ? competitor.statusName : selectedMarket.statusName }
              </div>
            </TableCell>
          )
        } */ }
      </TableRow>
      {
        expanded && (
          <ExpandedRow
            colSpan={ handleTableRowSpan() }
            competitor={ competitor }
            trainerName={ getPrettyName(competitor.trainer.name) }
            racingType={ racingType }
          />
        )
      }
    </>
  );

}
